import { useEffect, useState } from "react";
import { z } from "zod";
import { X, Download, CheckCircle2, Loader2, MessageCircle } from "lucide-react";
import { submitCatalogRequest, buildCatalogWhatsApp, openWhatsApp } from "../lib/api";
import { BRAND } from "../data/site";

const Schema = z.object({
    name: z.string().min(2, "Enter your full name"),
    email: z.string().email("Enter a valid email"),
    country: z.string().min(2, "Country is required"),
    whatsapp: z.string().min(6, "WhatsApp number is required"),
    company: z.string().optional().or(z.literal("")),
});

const initial = { name: "", email: "", country: "", whatsapp: "", company: "" };

export default function CatalogModal({ open, onClose }) {
    const [data, setData] = useState(initial);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle");
    const [waUrl, setWaUrl] = useState("");

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose?.();
        if (open) document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    const onChange = (e) => {
        setData({ ...data, [e.target.name]: e.target.value });
        if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: undefined });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const r = Schema.safeParse(data);
        if (!r.success) {
            const errs = {};
            r.error.issues.forEach((i) => (errs[i.path[0]] = i.message));
            setErrors(errs);
            return;
        }
        // Pre-open tab on user gesture for popup-blocker safety.
        const pre = window.open("about:blank", "_blank");
        setStatus("submitting");
        const wa = buildCatalogWhatsApp(data);
        // Fire-and-forget save; UX shouldn't block on it.
        submitCatalogRequest(data).catch((err) => {
            console.warn("Catalog save failed (continuing to WhatsApp):", err?.message || err);
        });
        // Trigger PDF catalog download.
        try {
            const a = document.createElement("a");
            a.href = BRAND.catalogPdf;
            a.download = "RP-Traders-Catalogue.pdf";
            a.target = "_blank";
            a.rel = "noopener noreferrer";
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (err) {
            console.warn("Catalog download trigger failed:", err);
        }
        openWhatsApp(pre, wa);
        setWaUrl(wa);
        setStatus("success");
    };

    return (
        <div
            data-testid="catalog-modal"
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl"
            >
                <button
                    onClick={onClose}
                    data-testid="catalog-close"
                    aria-label="Close"
                    className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full bg-black/5 text-ink hover:bg-black/10"
                >
                    <X className="h-5 w-5" />
                </button>

                {status === "success" ? (
                    <div className="p-8 sm:p-10">
                        <div className="grid h-12 w-12 place-items-center rounded-full bg-emerald-100">
                            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                        </div>
                        <h3 className="mt-5 font-display text-2xl font-bold">
                            ✓ Thank you. Your catalog is downloading.
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-ink-muted">
                            The RP Traders catalogue PDF is downloading to your device now. We&apos;ve
                            also opened a WhatsApp message with your request — please send it to
                            connect with our export team. If either didn&apos;t open, use the buttons
                            below.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <a
                                href={BRAND.catalogPdf}
                                download="RP-Traders-Catalogue.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="catalog-download-again"
                                className="inline-flex items-center gap-2 rounded-full border border-hairline bg-white px-5 py-3 text-sm font-semibold text-ink hover:bg-black/5"
                            >
                                <Download className="h-4 w-4" />
                                Download PDF again
                            </a>
                            <a
                                href={waUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="catalog-continue-wa"
                                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-sm font-semibold text-white"
                            >
                                <MessageCircle className="h-4 w-4" />
                                Open WhatsApp message
                            </a>
                            <button
                                onClick={onClose}
                                data-testid="catalog-done"
                                className="inline-flex items-center justify-center rounded-full bg-brand-red px-6 py-3 text-sm font-semibold text-white"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={onSubmit} data-testid="catalog-form" className="p-8 sm:p-10" noValidate>
                        <div className="flex items-center gap-3">
                            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-red/10 text-brand-red">
                                <Download className="h-6 w-6" />
                            </span>
                            <div>
                                <h3 className="font-display text-2xl font-bold tracking-tight">
                                    Download Product Catalog
                                </h3>
                                <p className="text-sm text-ink-muted">
                                    Tell us where to send it — PDF arrives within 24 hours.
                                </p>
                            </div>
                        </div>

                        <div className="mt-6 grid gap-4 sm:grid-cols-2">
                            <Field name="name" label="Full name *" value={data.name} onChange={onChange} error={errors.name} testid="cat-name" />
                            <Field name="company" label="Company" value={data.company} onChange={onChange} error={errors.company} testid="cat-company" />
                            <Field name="country" label="Country *" value={data.country} onChange={onChange} error={errors.country} testid="cat-country" />
                            <Field name="whatsapp" label="WhatsApp *" placeholder="+971 50 000 0000" value={data.whatsapp} onChange={onChange} error={errors.whatsapp} testid="cat-whatsapp" />
                            <div className="sm:col-span-2">
                                <Field name="email" type="email" label="Email *" value={data.email} onChange={onChange} error={errors.email} testid="cat-email" />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={status === "submitting"}
                            data-testid="cat-submit"
                            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-red px-6 py-3.5 text-sm font-semibold text-white disabled:opacity-60"
                        >
                            {status === "submitting" ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" /> Sending request…
                                </>
                            ) : (
                                <>
                                    <Download className="h-4 w-4" />
                                    Email me the catalog
                                </>
                            )}
                        </button>

                        {status === "error" && (
                            <p className="mt-3 text-xs text-brand-red">
                                We are experiencing a temporary issue. Please contact us via WhatsApp at +91 94910 85798.
                            </p>
                        )}
                    </form>
                )}
            </div>
        </div>
    );
}

function Field({ name, label, value, onChange, error, type = "text", placeholder, testid }) {
    return (
        <div>
            <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.18em] text-ink-muted">
                {label}
            </label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                data-testid={testid}
                className="w-full rounded-2xl border border-hairline bg-white px-4 py-3 text-sm focus:border-brand-red focus:outline-none"
            />
            {error && <p className="mt-1 text-xs text-brand-red">{error}</p>}
        </div>
    );
}
